<template>
  <div data-test="hero-section" :aria-label="t('main_page.hero_section')">
    <div class="relative hero">
      <picture v-if="backgroundUrl">
        <source :srcset="`${backgroundUrlDesktop}?w=1900&h=528&fit=fill&fm=webp&q=85`" media="(min-width: 768px)" />
        <img :src="`${backgroundUrl}?w=568&h=284&fit=fill&fm=webp&q=85`" :alt="title" class="relative w-full" />
      </picture>
      <div v-else class="image-fallback" :class="fallbackClass" />
      <div :class="overlayClass">
        <div class="flex flex-col md:absolute md:left-6 md:bottom-0.5 xl:bottom-6">
          <div
            class="absolute bottom-0 flex items-center p-6 md:relative md:pt-0 md:pr-0"
            :class="{ 'text-white': backgroundUrl }"
          >
            <img
              v-if="iconUrl"
              class="w-6 h-6 mr-2 md:w-12 md:h-12 md:mr-4 lg:w-16 lg:h-16"
              :src="iconUrl"
              :alt="title"
            />
            <component
              :is="largeTitle ? 'h1' : 'h2'"
              class="leading-tight font-heading"
              :class="{
                'text-2xl md:text-4xl lg:text-6xl 2xl:text-7xl': largeTitle,
                'text-xl md:text-2xl lg:text-4xl 2xl:text-5xl': !largeTitle,
              }"
            >
              {{ title }}
            </component>
          </div>
          <p
            v-if="!(mq.xsmall || mq.small)"
            class="px-6 pb-5 text-lg leading-6"
            :class="{ 'text-white': backgroundUrl }"
          >
            {{ description }}
          </p>
        </div>
      </div>
    </div>
    <div v-if="(mq.xsmall || mq.small) && description" class="p-6 bg-white">
      <p class="text-sm leading-6">{{ description }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { setCmsTemplateClasses } from '@/config/cms';

type Props = {
  title?: string;
  titleFontSize?: string;
  backgroundUrlDesktop?: string;
  backgroundUrlMobile?: string;
  iconUrl?: string;
  description?: string;
  overlay?: boolean;
  template?: string;
};

const props = withDefaults(defineProps<Props>(), {
  title: '',
  titleFontSize: 'large',
  backgroundUrlDesktop: '',
  backgroundUrlMobile: '',
  iconUrl: '',
  description: '',
  overlay: false,
  template: undefined,
});

const { mq } = useMediaQueries();
const { t } = useI18n();

const backgroundUrl = computed(() => props.backgroundUrlMobile || props.backgroundUrlDesktop);
const overlayClass = computed(() => {
  if (props.overlay) {
    return ['hero__overlay'];
  }
  return undefined;
});

const fallbackClass = computed(() => {
  return props.template ? setCmsTemplateClasses(props.template, 'hero').join(' ') : '';
});

const largeTitle = computed(() => props.titleFontSize === 'large');

useHead(() => ({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: `${backgroundUrl.value}?w=568&h=284&fit=fill&fm=webp&q=85`,
      media: '(max-width: 768px)',
    },
    {
      rel: 'preload',
      as: 'image',
      href: `${props.backgroundUrlDesktop}?w=1900&h=528&fit=fill&fm=webp&q=85`,
      media: '(min-width: 768px)',
    },
  ],
}));
</script>

<script lang="ts">
export default defineComponent({
  name: 'TheHero',
});
</script>

<style lang="scss" scoped>
.hero {
  height: 50vw;
  @screen md {
    height: calc(100vw * 0.278);
  }
  .image-fallback {
    @apply bg-gray-200;
  }

  &__overlay {
    @apply absolute min-w-full min-h-full bottom-0 bg-black bg-opacity-25;
  }
}
</style>
